<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useProductsStore } from 'dashboard/stores/products';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const props = defineProps({
  sectors: { type: Array, default: () => [] },
  selectedSector: { type: Object, default: null },
});

const emit = defineEmits(['selectProduct']);

const { t } = useI18n();
const store = useProductsStore();
const uiFlags = computed(() => store.uiFlags);
const activeProductId = ref(null);

const products = computed(() => {
  if (!props.selectedSector) return store.records;
  return store.records.filter(p => p.sector_id === props.selectedSector.id);
});

const sectorOptions = computed(() =>
  props.sectors.map(s => ({ value: s.id, label: s.name }))
);

const showFormModal = ref(false);
const showDeleteModal = ref(false);
const selectedProduct = ref(null);

const form = reactive({
  name: '',
  description: '',
  sector_id: '',
  active: true,
});
const isFormInvalid = computed(() => !form.name.trim() || !form.sector_id);

const resetForm = () => {
  form.name = '';
  form.description = '';
  form.sector_id = props.selectedSector?.id || '';
  form.active = true;
};

const openAdd = () => {
  selectedProduct.value = null;
  resetForm();
  showFormModal.value = true;
};

const openEdit = product => {
  selectedProduct.value = product;
  form.name = product.name;
  form.description = product.description || '';
  form.sector_id = product.sector_id;
  form.active = product.active;
  showFormModal.value = true;
};

const closeFormModal = () => {
  showFormModal.value = false;
};

const submitForm = async () => {
  if (isFormInvalid.value) return;
  try {
    if (selectedProduct.value) {
      await store.update({ id: selectedProduct.value.id, ...form });
    } else {
      await store.create({ ...form });
    }
    useAlert(t('PRODUCTS_MGMT.PRODUCTS.SAVED'));
    closeFormModal();
  } catch (error) {
    useAlert(error?.message || t('PRODUCTS_MGMT.PRODUCTS.SAVE_ERROR'));
  }
};

const openDelete = product => {
  selectedProduct.value = product;
  showDeleteModal.value = true;
};

const confirmDelete = async () => {
  try {
    await store.delete(selectedProduct.value.id);
    useAlert(t('PRODUCTS_MGMT.PRODUCTS.DELETED'));
  } catch (error) {
    useAlert(error?.message || t('PRODUCTS_MGMT.PRODUCTS.DELETE_ERROR'));
  } finally {
    showDeleteModal.value = false;
  }
};

const selectProduct = product => {
  activeProductId.value = product.id;
  emit('selectProduct', product);
};

watch(
  () => props.selectedSector,
  () => {
    activeProductId.value = null;
  }
);

onMounted(() => store.get());
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
  >
    <div class="flex items-center justify-between">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('PRODUCTS_MGMT.PRODUCTS.TITLE') }}
        <span v-if="selectedSector" class="text-sm font-normal text-n-slate-11">
          {{
            t('PRODUCTS_MGMT.PRODUCTS.IN_SECTOR', {
              sector: selectedSector.name,
            })
          }}
        </span>
      </h3>
      <Button
        :label="t('PRODUCTS_MGMT.PRODUCTS.ADD')"
        size="sm"
        @click="openAdd"
      />
    </div>
    <BaseTable
      :headers="[
        t('PRODUCTS_MGMT.PRODUCTS.TITLE'),
        t('PRODUCTS_MGMT.SECTORS.TITLE'),
        t('COMMON_MGMT.ACTIVE'),
        '',
      ]"
      :items="products"
      :no-data-message="t('PRODUCTS_MGMT.PRODUCTS.NO_DATA')"
    >
      <template #row="{ items }">
        <BaseTableRow
          v-for="product in items"
          :key="product.id"
          :item="product"
          class="cursor-pointer"
          :class="{ 'bg-n-blue-2': activeProductId === product.id }"
          @click="selectProduct(product)"
        >
          <template #default>
            <BaseTableCell>
              <span class="text-body-main text-n-slate-12">{{
                product.name
              }}</span>
            </BaseTableCell>
            <BaseTableCell>
              <span class="text-body-main text-n-slate-11">{{
                product.sector_name
              }}</span>
            </BaseTableCell>
            <BaseTableCell>
              <span
                class="px-2 py-0.5 text-xs rounded-full"
                :class="
                  product.active
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-slate-3 text-n-slate-11'
                "
              >
                {{
                  product.active
                    ? t('COMMON_MGMT.ACTIVE')
                    : t('COMMON_MGMT.INACTIVE')
                }}
              </span>
            </BaseTableCell>
            <BaseTableCell align="end">
              <div class="flex justify-end gap-2" @click.stop>
                <Button
                  icon="i-woot-edit-pen"
                  slate
                  sm
                  @click="openEdit(product)"
                />
                <Button
                  icon="i-woot-bin"
                  slate
                  sm
                  @click="openDelete(product)"
                />
              </div>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>

    <woot-modal v-model:show="showFormModal" :on-close="closeFormModal">
      <div class="flex flex-col w-full gap-4 p-6">
        <h3 class="text-lg font-medium text-n-slate-12">
          {{
            selectedProduct
              ? t('PRODUCTS_MGMT.PRODUCTS.EDIT')
              : t('PRODUCTS_MGMT.PRODUCTS.ADD')
          }}
        </h3>
        <Input
          v-model="form.name"
          :placeholder="t('PRODUCTS_MGMT.PRODUCTS.NAME_PLACEHOLDER')"
          autofocus
        />
        <SelectInput
          v-model="form.sector_id"
          :options="sectorOptions"
          :placeholder="t('PRODUCTS_MGMT.PRODUCTS.SECTOR_PLACEHOLDER')"
        />
        <TextArea
          v-model="form.description"
          :placeholder="t('PRODUCTS_MGMT.SECTORS.DESCRIPTION_PLACEHOLDER')"
        />
        <div class="flex items-center gap-2">
          <Switch v-model="form.active" />
          <span class="text-sm text-n-slate-11">{{
            t('COMMON_MGMT.ACTIVE')
          }}</span>
        </div>
        <div class="flex justify-end gap-2">
          <Button
            :label="t('COMMON_MGMT.CANCEL')"
            variant="link"
            @click="closeFormModal"
          />
          <Button
            :label="t('COMMON_MGMT.SAVE')"
            :disabled="isFormInvalid"
            :is-loading="uiFlags.creatingItem || uiFlags.updatingItem"
            @click="submitForm"
          />
        </div>
      </div>
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
      :on-confirm="confirmDelete"
      :title="t('PRODUCTS_MGMT.PRODUCTS.DELETE_TITLE')"
      :message="t('PRODUCTS_MGMT.PRODUCTS.DELETE_MESSAGE')"
      :message-value="selectedProduct?.name"
      :confirm-text="t('COMMON_MGMT.DELETE')"
      :reject-text="t('COMMON_MGMT.CANCEL')"
    />
  </div>
</template>
